import {
  ArrowLeft,
  BookOpen,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  Divide,
  Eye,
  Info,
  Loader2,
  Minus,
  MoveLeft,
  MoveRight,
  Plus,
  Search,
  SlidersHorizontal,
  UserRound,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import type {
  CreateCustomKpiRequest,
  CustomKpiBaseMetricOption,
  CustomKpiCatalogResponse,
  CustomKpiDefinition,
  CustomKpiUnitOption,
  UpdateCustomKpiRequest,
} from '@/types/template';

interface CustomKpiEditorProps {
  initialValue?: CustomKpiDefinition;
  catalog?: CustomKpiCatalogResponse;
  nextVersion?: number;
  onSubmit: (data: CreateCustomKpiRequest | UpdateCustomKpiRequest) => Promise<void> | void;
  onCancel?: () => void;
  isSaving?: boolean;
  apiError?: string | null;
  useExampleDefaults?: boolean;
}

type Direction = 'higher' | 'lower';
type MetricPattern = 'costPer' | 'rate' | 'ratio' | 'sum';

const metricPatterns: Array<{
  id: MetricPattern;
  label: string;
  hint: string;
  icon: ReactNode;
}> = [
  {
    id: 'costPer',
    label: 'Cost per event',
    hint: 'Media cost divided by an outcome',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  {
    id: 'rate',
    label: 'Rate',
    hint: 'An outcome divided by reach or impressions',
    icon: <MoveRight className="h-4 w-4" />,
  },
  {
    id: 'ratio',
    label: 'Ratio',
    hint: 'Compare two business signals',
    icon: <Eye className="h-4 w-4" />,
  },
  {
    id: 'sum',
    label: 'Sum',
    hint: 'Add compatible delivery signals',
    icon: <SlidersHorizontal className="h-4 w-4" />,
  },
];

const operatorDisplay: Record<string, string> = {
  '+': '+',
  '-': '-',
  '*': 'x',
  '/': '/',
  '(': '(',
  ')': ')',
};

const operatorPreviewDisplay: Record<string, string> = {
  '+': '+',
  '-': '-',
  '*': 'x',
  '/': '/',
  '(': '(',
  ')': ')',
};

function getInitialUnit(initialValue: CustomKpiDefinition | undefined, units: CustomKpiUnitOption[]) {
  if (initialValue?.unit) return initialValue.unit;
  return units.find((unit) => unit.id === 'costPer')?.id ?? units[0]?.id ?? '';
}

function getInitialFormulaTokens(
  initialValue: CustomKpiDefinition | undefined,
  metrics: CustomKpiBaseMetricOption[]
): string[] {
  if (initialValue?.formula) return initialValue.formula.split(/\s+/).filter(Boolean);

  const spend = metrics.find((metric) => metric.id === 'spend');
  const completedViews =
    metrics.find((metric) => metric.id === 'video100Percent') ??
    metrics.find((metric) => /completed|100/i.test(metric.label));

  if (spend && completedViews) return [spend.id, '/', completedViews.id];
  return [];
}

function formatPlatformLabel(platform: string): string {
  const labels: Record<string, string> = {
    meta: 'Meta',
    facebook: 'Facebook',
    instagram: 'Instagram',
    google: 'Google',
    tiktok: 'TikTok',
    linkedin: 'LinkedIn',
    pinterest: 'Pinterest',
    amazon: 'Amazon',
    ttd: 'TTD',
  };
  return labels[platform] ?? platform;
}

function getAvailablePlatforms(metric: CustomKpiBaseMetricOption | undefined): string[] {
  if (!metric) return [];
  return Object.entries(metric.platformAvailability)
    .filter(([, availability]) => availability !== 'unavailable')
    .map(([platform]) => formatPlatformLabel(platform));
}

function getMetricLabel(metrics: CustomKpiBaseMetricOption[], token: string): string {
  return metrics.find((metric) => metric.id === token)?.label ?? token;
}

function getMetricGroup(metrics: CustomKpiBaseMetricOption[], token: string): string {
  return metrics.find((metric) => metric.id === token)?.category ?? 'Metric';
}

function isOperator(token: string): boolean {
  return Object.prototype.hasOwnProperty.call(operatorDisplay, token);
}

function isNumericConstant(token: string): boolean {
  return token.trim() !== '' && Number.isFinite(Number(token));
}

function getFormulaPreview(metrics: CustomKpiBaseMetricOption[], tokens: string[]): string {
  if (tokens.length === 0) return 'Add metrics and operators to define the formula.';
  return tokens
    .map((token) => operatorPreviewDisplay[token] ?? getMetricLabel(metrics, token))
    .join(' ');
}

function groupMetricsByCategory(
  metrics: CustomKpiBaseMetricOption[]
): Array<{ category: string; metrics: CustomKpiBaseMetricOption[] }> {
  const groupedMetrics = metrics.reduce<Map<string, CustomKpiBaseMetricOption[]>>((groups, metric) => {
    const category = metric.category || 'Metric';
    groups.set(category, [...(groups.get(category) ?? []), metric]);
    return groups;
  }, new Map());

  return Array.from(groupedMetrics, ([category, categoryMetrics]) => ({
    category,
    metrics: categoryMetrics,
  }));
}

function StepSection({
  number,
  title,
  description,
  children,
}: {
  number: number;
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="border-b border-border-subtle px-4 py-5 last:border-b-0 sm:px-5">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-bold text-inverse">
          {number}
        </span>
        <div>
          <h3 className="text-sm font-bold text-primary">{title}</h3>
          <p className="mt-0.5 text-xs text-secondary">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function ValidationItem({ valid, children }: { valid: boolean; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-sm text-secondary">
      <CheckCircle2
        className={cn('h-4 w-4 shrink-0', valid ? 'text-success' : 'text-muted')}
        aria-hidden="true"
      />
      <span>{children}</span>
    </div>
  );
}

export function CustomKpiEditor({
  initialValue,
  catalog,
  nextVersion,
  onSubmit,
  onCancel,
  isSaving = false,
  apiError = null,
  useExampleDefaults = false,
}: CustomKpiEditorProps) {
  const units = useMemo(() => catalog?.units ?? [], [catalog]);
  const baseMetrics = useMemo(() => catalog?.baseMetrics ?? [], [catalog]);
  const operators = useMemo(() => catalog?.operators ?? ['+', '-', '*', '/', '(', ')'], [catalog]);

  const [name, setName] = useState(
    initialValue?.name ?? (useExampleDefaults ? 'Cost per Completed View' : '')
  );
  const [shortLabel, setShortLabel] = useState(
    initialValue?.shortLabel ?? (useExampleDefaults ? 'CPCV' : '')
  );
  const [unit, setUnit] = useState(() =>
    initialValue || useExampleDefaults ? getInitialUnit(initialValue, units) : ''
  );
  const [direction, setDirection] = useState<Direction>(
    initialValue?.higherIsBetter === true ? 'higher' : 'lower'
  );
  const [formulaTokens, setFormulaTokens] = useState<string[]>(() =>
    initialValue || useExampleDefaults ? getInitialFormulaTokens(initialValue, baseMetrics) : []
  );
  const [formulaEdited, setFormulaEdited] = useState(Boolean(initialValue));
  const [metricPattern, setMetricPattern] = useState<MetricPattern>(() => {
    if (initialValue?.unit === 'percentage') return 'rate';
    if (initialValue?.formula?.includes('+')) return 'sum';
    return 'costPer';
  });
  const [metricSearch, setMetricSearch] = useState('');
  const [constantValue, setConstantValue] = useState('');
  const [description, setDescription] = useState(initialValue?.description ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [focusedMetricId, setFocusedMetricId] = useState<string | null>(
    initialValue?.baseMetricIds?.[0] ??
      (useExampleDefaults
        ? baseMetrics.find((metric) => metric.id === 'video100Percent')?.id ?? baseMetrics[0]?.id
        : null) ??
      null
  );

  const selectedUnit = units.find((option) => option.id === unit);
  const focusedMetric = baseMetrics.find((metric) => metric.id === focusedMetricId);
  const formula = formulaTokens.join(' ');
  const selectedMetricTokens = formulaTokens.filter((token) =>
    baseMetrics.some((metric) => metric.id === token)
  );
  const selectedMetricCount = new Set(selectedMetricTokens).size;
  const hasOperator = formulaTokens.some((token) => ['+', '-', '*', '/'].includes(token));
  const isFormulaValid =
    formulaTokens.length >= 3 && selectedMetricCount >= 2 && hasOperator && !apiError;
  const unitsCompatible = selectedMetricCount >= 2;
  const canCreate =
    name.trim().length > 0 &&
    shortLabel.trim().length > 0 &&
    Boolean(selectedUnit) &&
    isFormulaValid &&
    unitsCompatible &&
    !isSaving;

  const filteredMetrics = useMemo(() => {
    const search = metricSearch.trim().toLowerCase();
    return search
      ? baseMetrics.filter((metric) =>
          [metric.label, metric.id, metric.category, metric.description]
            .join(' ')
            .toLowerCase()
            .includes(search)
        )
      : baseMetrics;
  }, [baseMetrics, metricSearch]);
  const groupedMetrics = useMemo(() => groupMetricsByCategory(filteredMetrics), [filteredMetrics]);
  const formulaPreview = getFormulaPreview(baseMetrics, formulaTokens);

  const insertFormulaToken = (token: string) => {
    setFormulaTokens((current) =>
      useExampleDefaults && !formulaEdited && !initialValue ? [token] : [...current, token]
    );
    setFormulaEdited(true);
    setErrors((current) => ({ ...current, formula: '' }));
  };

  const insertMetric = (metric: CustomKpiBaseMetricOption) => {
    setFocusedMetricId(metric.id);
    insertFormulaToken(metric.id);
  };

  const removeFormulaToken = (index: number) => {
    setFormulaEdited(true);
    setFormulaTokens((current) => current.filter((_, currentIndex) => currentIndex !== index));
  };

  const moveFormulaToken = (index: number, direction: -1 | 1) => {
    setFormulaEdited(true);
    setFormulaTokens((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = [...current];
      const currentToken = next[index];
      const targetToken = next[nextIndex];
      if (currentToken === undefined || targetToken === undefined) return current;
      next[index] = targetToken;
      next[nextIndex] = currentToken;
      return next;
    });
  };

  const addConstant = () => {
    const normalized = constantValue.trim();
    if (!normalized || !Number.isFinite(Number(normalized))) {
      setErrors((current) => ({ ...current, constant: 'Enter a numeric constant' }));
      return;
    }
    insertFormulaToken(normalized);
    setConstantValue('');
    setErrors((current) => ({ ...current, constant: '' }));
  };

  const clearFormula = () => {
    setFormulaEdited(true);
    setFormulaTokens([]);
    setErrors((current) => ({ ...current, formula: 'Formula is required' }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!name.trim()) nextErrors.name = 'Name is required';
    if (!shortLabel.trim()) nextErrors.shortLabel = 'Short label is required';
    if (!units.some((option) => option.id === unit)) {
      nextErrors.unit = 'Select a unit from the catalog.';
    }
    if (formulaTokens.length === 0) {
      nextErrors.formula = 'Formula is required';
    } else if (!isFormulaValid) {
      nextErrors.formula = 'Choose at least 2 metrics and an operator.';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    await onSubmit({
      name: name.trim(),
      shortLabel: shortLabel.trim(),
      unit,
      higherIsBetter: direction === 'higher',
      formula,
      description: description.trim() || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" aria-label="Custom KPI editor">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-start gap-4">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-border-subtle bg-surface text-secondary shadow-sm transition-colors hover:bg-surface-subtle hover:text-primary focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
              aria-label="Back to Custom KPI list"
              title="Back to Custom KPI list"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          )}
          <div>
            <h2 className="text-2xl font-bold leading-8 text-primary">
              {initialValue ? 'Create KPI version' : 'Create Custom KPI'}
            </h2>
            <p className="mt-1 text-sm text-secondary">
              Define the calculation and meaning of your KPI.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<BookOpen className="h-4 w-4" />}
        >
          Learn about KPIs
        </Button>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-sm">
          <StepSection
            number={1}
            title="KPI basics"
            description="Give your KPI a name, label, and how to interpret it."
          >
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-[minmax(220px,1.25fr)_minmax(120px,.65fr)_minmax(220px,1fr)_minmax(230px,1fr)]">
              <Input
                label="Name"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setErrors((current) => ({ ...current, name: '' }));
                }}
                placeholder="Cost per Completed View"
                error={!!errors.name}
                errorMessage={errors.name}
                disabled={isSaving}
              />
              <Input
                label="Short label"
                value={shortLabel}
                onChange={(event) => {
                  setShortLabel(event.target.value);
                  setErrors((current) => ({ ...current, shortLabel: '' }));
                }}
                placeholder="CPCV"
                error={!!errors.shortLabel}
                errorMessage={errors.shortLabel}
                disabled={isSaving}
                helperText="Used in tables and charts"
              />
              <div>
                <label
                  htmlFor="custom-kpi-unit"
                  className="mb-2 block text-sm font-medium text-secondary"
                >
                  Unit
                </label>
                <select
                  id="custom-kpi-unit"
                  value={unit}
                  onChange={(event) => {
                    const nextUnit = units.find((option) => option.id === event.target.value);
                    setUnit(event.target.value);
                    if (nextUnit) setDirection(nextUnit.defaultDirection);
                    setErrors((current) => ({ ...current, unit: '' }));
                  }}
                  className="h-11 w-full rounded-md border border-border-subtle bg-surface px-4 text-sm text-primary focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12 disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={isSaving || !catalog}
                  aria-invalid={!!errors.unit}
                  aria-describedby={errors.unit ? 'custom-kpi-unit-error' : undefined}
                >
                  <option value="">Select unit</option>
                  {units.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-muted">
                  {selectedUnit
                    ? `Preview format: ${selectedUnit.displayFormat || selectedUnit.symbol}`
                    : 'Select a catalog unit'}
                </p>
                {errors.unit && (
                  <p id="custom-kpi-unit-error" className="mt-2 text-xs text-danger" role="alert">
                    {errors.unit}
                  </p>
                )}
              </div>
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-sm font-medium text-secondary">
                  Interpretation
                  <Info className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                </div>
                <div className="grid h-11 grid-cols-2 rounded-md border border-border-subtle bg-surface p-1">
                  {(['higher', 'lower'] as Direction[]).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setDirection(option)}
                      className={cn(
                        'rounded text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/20',
                        direction === option
                          ? 'border border-brand bg-brand/10 text-brand shadow-sm'
                          : 'text-secondary hover:bg-surface-subtle'
                      )}
                      aria-pressed={direction === option}
                    >
                      {option === 'higher' ? 'Higher is better' : 'Lower is better'}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </StepSection>

          <StepSection
            number={2}
            title="Formula setup"
            description="Choose how you want to calculate this KPI."
          >
            <p className="sr-only">What are you measuring?</p>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              {metricPatterns.map((pattern) => (
                <button
                  key={pattern.id}
                  type="button"
                  onClick={() => setMetricPattern(pattern.id)}
                  className={cn(
                    'min-h-[84px] rounded-xl border px-4 py-3 text-left transition-all focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12',
                    metricPattern === pattern.id
                      ? 'border-brand bg-brand/10 shadow-[0_0_0_1px_rgba(61,90,254,0.25)]'
                      : 'border-border-subtle bg-surface hover:border-border-strong hover:bg-surface-subtle'
                  )}
                  aria-pressed={metricPattern === pattern.id}
                >
                  <span className="flex items-center gap-2 text-sm font-bold text-primary">
                    <span
                      className={cn(
                        'text-secondary',
                        metricPattern === pattern.id && 'text-brand'
                      )}
                    >
                      {pattern.icon}
                    </span>
                    {pattern.label}
                  </span>
                  <span className="mt-1.5 block text-xs leading-5 text-secondary">
                    {pattern.hint}
                  </span>
                </button>
              ))}
            </div>
          </StepSection>

          <StepSection
            number={3}
            title="Build your formula"
            description="Use metrics, operators, and constants to define the calculation."
          >
            <div className="rounded-xl border border-border-subtle bg-surface">
              <div className="flex flex-col gap-3 border-b border-border-subtle p-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <div className="text-xs font-semibold text-muted">Formula</div>
                  <input
                    id="custom-kpi-formula-preview"
                    aria-label="Formula preview"
                    readOnly
                    value={formula}
                    className="sr-only"
                    title={formula}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {formulaTokens.length > 0 ? (
                      formulaTokens.map((token, index) =>
                        isOperator(token) ? (
                          <span
                            key={`${token}-${index}`}
                            className="inline-flex h-10 min-w-8 items-center justify-center rounded-md text-sm font-bold text-primary"
                          >
                            {operatorDisplay[token]}
                          </span>
                        ) : (
                          <span
                            key={`${token}-${index}`}
                            className="inline-flex min-h-12 items-center gap-2 rounded-lg border border-border-subtle bg-surface px-3 py-2 shadow-sm"
                          >
                            <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted" />
                            <span className="min-w-0">
                              <span className="block text-sm font-semibold leading-4 text-primary">
                                {isNumericConstant(token) ? token : getMetricLabel(baseMetrics, token)}
                              </span>
                              {!isNumericConstant(token) && (
                                <span className="block text-xs text-muted">
                                  {getMetricGroup(baseMetrics, token)}
                                </span>
                              )}
                            </span>
                            <span className="inline-flex items-center gap-0.5">
                              <button
                                type="button"
                                onClick={() => moveFormulaToken(index, -1)}
                                className="rounded p-0.5 text-muted hover:bg-surface-subtle hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                                aria-label={`Move ${token} left`}
                                title={`Move ${token} left`}
                                disabled={isSaving || index === 0}
                              >
                                <MoveLeft className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveFormulaToken(index, 1)}
                                className="rounded p-0.5 text-muted hover:bg-surface-subtle hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                                aria-label={`Move ${token} right`}
                                title={`Move ${token} right`}
                                disabled={isSaving || index === formulaTokens.length - 1}
                              >
                                <MoveRight className="h-3.5 w-3.5" />
                              </button>
                            </span>
                            <button
                              type="button"
                              onClick={() => removeFormulaToken(index)}
                              className="rounded p-0.5 text-muted hover:bg-danger/10 hover:text-danger"
                              aria-label={`Remove ${token} token`}
                              title={`Remove ${token} token`}
                              disabled={isSaving}
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        )
                      )
                    ) : (
                      <span className="rounded-lg border border-dashed border-border-subtle px-3 py-2 text-sm text-secondary">
                        Start with a base metric, such as Spend.
                      </span>
                    )}
                  </div>
                </div>
                <Button type="button" variant="secondary" size="sm" onClick={clearFormula}>
                  Clear
                </Button>
              </div>

              <div className="flex flex-wrap items-end gap-2 p-3">
                {operators.map((operator) => (
                  <button
                    key={operator}
                    type="button"
                    onClick={() => insertFormulaToken(operator)}
                    className="inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-border-subtle bg-surface px-3 text-sm font-bold text-primary shadow-sm hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                    aria-label={`Insert operator ${operator}`}
                    disabled={isSaving || !catalog}
                  >
                    {operator === '/' ? (
                      <Divide className="h-4 w-4" />
                    ) : operator === '+' ? (
                      <Plus className="h-4 w-4" />
                    ) : operator === '-' ? (
                      <Minus className="h-4 w-4" />
                    ) : (
                      operatorDisplay[operator] ?? operator
                    )}
                  </button>
                ))}
                <div className="grid min-w-[220px] grid-cols-[minmax(0,1fr)_auto] items-start gap-2">
                  <Input
                    label="Numeric constant"
                    value={constantValue}
                    onChange={(event) => {
                      setConstantValue(event.target.value);
                      setErrors((current) => ({ ...current, constant: '' }));
                    }}
                    placeholder="1.5"
                    error={!!errors.constant}
                    errorMessage={errors.constant}
                    disabled={isSaving || !catalog}
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-7 shrink-0"
                    onClick={addConstant}
                    disabled={isSaving || !catalog}
                  >
                    Add constant
                  </Button>
                </div>
              </div>
            </div>

            {errors.formula && (
              <p className="mt-2 text-xs text-danger" role="alert">
                {errors.formula}
              </p>
            )}
            <div className="mt-3 rounded-lg border border-border-subtle bg-surface-subtle/60 px-3 py-2">
              <div className="text-xs font-semibold uppercase text-muted">Calculated as</div>
              <p className="mt-1 text-sm text-secondary">{formulaPreview}</p>
            </div>

            <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(280px,1fr)_minmax(320px,1fr)]">
              <div className="rounded-xl border border-border-subtle bg-surface">
                <div className="border-b border-border-subtle p-3">
                  <label
                    htmlFor="custom-kpi-metric-search"
                    className="mb-2 block text-sm font-medium text-secondary"
                  >
                    Search base metrics
                  </label>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                    <input
                      id="custom-kpi-metric-search"
                      value={metricSearch}
                      onChange={(event) => setMetricSearch(event.target.value)}
                      placeholder="Search metrics..."
                      className="h-10 w-full rounded-md border border-border-subtle bg-surface pl-9 pr-3 text-sm text-primary placeholder:text-muted focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                      disabled={isSaving || !catalog}
                    />
                  </div>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {groupedMetrics.map((group) => (
                    <div key={group.category} className="border-b border-border-subtle last:border-b-0">
                      <div className="bg-surface-subtle/60 px-3 py-2 text-xs font-bold text-secondary">
                        {group.category}
                      </div>
                      {group.metrics.map((metric) => {
                        const selected = formulaTokens.includes(metric.id);
                        return (
                          <button
                            key={metric.id}
                            type="button"
                            onClick={() => insertMetric(metric)}
                            onFocus={() => setFocusedMetricId(metric.id)}
                            className={cn(
                              'flex w-full items-center justify-between gap-3 border-t border-border-subtle px-3 py-2 text-left transition-colors hover:bg-surface-subtle',
                              selected && 'bg-brand/10 text-brand'
                            )}
                            disabled={isSaving || !catalog}
                            title={metric.description}
                          >
                            <span className="flex min-w-0 items-center gap-2">
                              {selected && <Check className="h-4 w-4 shrink-0 text-brand" />}
                              <span className="min-w-0">
                                <span className="block truncate text-sm font-semibold text-primary">
                                  {metric.label}
                                </span>
                              </span>
                            </span>
                            <span className="shrink-0 rounded-full bg-surface-subtle px-2 py-0.5 text-xs font-medium text-secondary">
                              {metric.unit} / {metric.aggregation}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-border-subtle bg-surface p-4">
                {focusedMetric ? (
                  <div>
                    <div className="flex items-start justify-between gap-3">
                      <h4 className="text-base font-bold text-primary">{focusedMetric.label}</h4>
                      <span className="rounded-full bg-surface-subtle px-2 py-0.5 text-xs font-semibold text-secondary">
                        {focusedMetric.category}
                      </span>
                    </div>
                    <dl className="mt-4 space-y-4 text-sm">
                      <div>
                        <dt className="text-xs font-bold text-muted">Unit</dt>
                        <dd className="mt-1 text-secondary">
                          {focusedMetric.unit} ({focusedMetric.aggregation})
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-bold text-muted">Platforms</dt>
                        <dd className="mt-2 flex flex-wrap gap-2">
                          {getAvailablePlatforms(focusedMetric).map((platform) => (
                            <span
                              key={platform}
                              className="rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold text-brand"
                            >
                              {platform}
                            </span>
                          ))}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs font-bold text-muted">Definition</dt>
                        <dd className="mt-1 text-secondary">{focusedMetric.description}</dd>
                      </div>
                      <div>
                        <dt className="text-xs font-bold text-muted">Notes</dt>
                        <dd className="mt-1 text-secondary">
                          Supported across connected platforms where the metric is available.
                        </dd>
                      </div>
                    </dl>
                  </div>
                ) : (
                  <p className="text-sm text-secondary">
                    Select a metric to inspect its unit, platform support, and missing-data behavior.
                  </p>
                )}
              </div>
            </div>
          </StepSection>

          <StepSection
            number={4}
            title="Guidance (optional)"
            description="Add a description to help others understand when and how to use this KPI."
          >
            <div>
              <label htmlFor="custom-kpi-description" className="sr-only">
                Guidance
              </label>
              <textarea
                id="custom-kpi-description"
                value={description}
                maxLength={500}
                onChange={(event) => setDescription(event.target.value)}
                className="min-h-[76px] w-full rounded-md border border-border-subtle bg-surface px-4 py-3 text-sm text-primary placeholder:text-muted focus-visible:border-brand/55 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/12"
                placeholder="Explain the purpose of this KPI and any important context..."
                disabled={isSaving}
              />
              <div className="mt-1 text-right text-xs text-muted">{description.length} / 500</div>
            </div>
          </StepSection>

          <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            {initialValue && nextVersion ? (
              <p className="text-sm text-secondary">
                Creates v{nextVersion}. Existing templates stay pinned to their current versions.
              </p>
            ) : (
              <span aria-hidden="true" />
            )}
            <div className="flex items-center justify-end gap-3">
              {onCancel && (
                <Button type="button" variant="secondary" onClick={onCancel} disabled={isSaving}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                disabled={isSaving || !catalog}
                aria-disabled={!canCreate}
                className={!canCreate ? 'bg-surface-subtle text-muted hover:bg-surface-subtle' : undefined}
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSaving ? 'Creating...' : initialValue ? 'Create version' : 'Create KPI'}
              </Button>
            </div>
          </div>
        </div>

        <aside className="rounded-xl border border-border-subtle bg-surface p-5 shadow-sm xl:sticky xl:top-6">
          <div>
            <h3 className="text-sm font-bold text-primary">Live preview</h3>
            <p className="mt-1 text-xs text-secondary">See how your KPI will be displayed.</p>
          </div>
          <div className="mt-6 space-y-5">
            <div className="flex gap-3">
              <UserRound className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-xs font-bold text-muted">Name</div>
                <div className="mt-1 text-sm font-semibold text-primary">{name || 'Untitled KPI'}</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-xs font-bold text-muted">Short label</div>
                <div className="mt-1 text-sm font-semibold text-primary">{shortLabel || 'KPI'}</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Divide className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-xs font-bold text-muted">Formula</div>
                <div className="mt-1 text-sm font-semibold text-primary">{formulaPreview}</div>
              </div>
            </div>
            <div className="flex gap-3">
              <Eye className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-xs font-bold text-muted">Unit</div>
                <div className="mt-1 text-sm font-semibold text-primary">
                  {selectedUnit?.label ?? 'Select unit'}
                </div>
                {selectedUnit && (
                  <div className="text-xs text-secondary">{selectedUnit.displayFormat}</div>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
              <div>
                <div className="text-xs font-bold text-muted">Interpretation</div>
                <span className="mt-1 inline-flex rounded-full bg-brand/10 px-2 py-1 text-xs font-semibold text-brand">
                  {direction === 'higher' ? 'Higher is better' : 'Lower is better'}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-6 border-t border-border-subtle pt-5">
            <h4 className="text-xs font-bold text-primary">What it means</h4>
            <p className="mt-2 text-sm leading-6 text-secondary">
              {description ||
                `Shows how much you're spending for each completed view. ${
                  direction === 'lower'
                    ? 'Lower values indicate more cost-efficient performance.'
                    : 'Higher values indicate stronger performance.'
                }`}
            </p>
          </div>

          <div className="mt-5 rounded-xl border border-border-subtle bg-surface p-4">
            <h4 className="text-sm font-bold text-primary">Validation</h4>
            <div className="mt-3 space-y-2">
              <ValidationItem valid={isFormulaValid}>Formula valid</ValidationItem>
              <ValidationItem valid={selectedMetricCount >= 2}>
                {selectedMetricCount} metrics selected
              </ValidationItem>
              <ValidationItem valid={selectedMetricCount > 0}>Supported across platforms</ValidationItem>
              <ValidationItem valid={unitsCompatible}>Units are compatible</ValidationItem>
            </div>
          </div>

          {apiError && (
            <div
              className="mt-4 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger"
              role="alert"
            >
              Formula is invalid: {apiError}
            </div>
          )}

          <div className="mt-4 rounded-xl border border-border-subtle bg-surface-subtle/60 px-3 py-3 text-xs leading-5 text-secondary">
            Changes are saved as you edit. You can review and adjust before creating.
          </div>
        </aside>
      </div>
    </form>
  );
}
